import type { LegalDocPair } from '../types'

/**
 * Política de uso aceptable: qué no se puede hacer con véltica.
 *
 * Es el documento al que apuntan los términos cuando hablan de retirar
 * contenido o cerrar cuentas, y el que respalda lo que Stripe y los
 * registradores de dominios exigen a una plataforma que aloja sitios de terceros.
 */
export const AUP: LegalDocPair = {
  en: {
    title: 'Acceptable Use Policy',
    summary: 'What you may not do with véltica, and how we deal with violations.',
    intro: [
      'This Acceptable Use Policy (the “AUP”) is part of the [Terms of Service](doc:terms). It applies to your Account, your Sites, your Customer Content and everything you do with the Platform, including what your End Users can do through it. We may update it as described in the Terms.',
    ],
    sections: [
      {
        title: 'The basic rule',
        blocks: [
          'Use the Platform lawfully and in good faith. You may not use it, and you may not allow others to use it, in a way that harms people, breaks the law, violates other people’s rights or damages the Platform. The examples below illustrate this rule; they are not a complete list.',
        ],
      },
      {
        title: 'Prohibited content',
        blocks: [
          'You may not publish, store, link to or distribute content that:',
          {
            list: [
              'sexually exploits or endangers minors in any way;',
              'is illegal, or promotes, facilitates or celebrates illegal activity;',
              'threatens, harasses or incites violence against people or groups, or promotes terrorism or violent extremism;',
              'discriminates against or incites hatred of people because of race, ethnicity, national origin, religion, sex, gender, sexual orientation, disability or another protected characteristic;',
              'is fraudulent, deceptive or misleading, including phishing, impersonation of a person or organization, fake reviews, counterfeit goods and pyramid or Ponzi schemes;',
              'infringes copyright, trademarks, trade secrets, privacy or other rights of others;',
              'contains someone’s private information without permission, including intimate images shared without consent;',
              'is sexually explicit or offers adult sexual services;',
              'sells or promotes illegal drugs, weapons, or other goods or services that are illegal where you or your customers are located; or',
              'contains or distributes malware, spyware or code intended to harm devices or steal data.',
            ],
          },
        ],
      },
      {
        title: 'Prohibited behavior',
        blocks: [
          'You may not use the Platform to:',
          {
            list: [
              'send spam or unsolicited commercial messages, or use forms or bookings to send messages to people who did not ask for them;',
              'collect credentials, payment card numbers or sensitive data from people through forms designed to deceive or without a lawful purpose and required notices;',
              'attack, overload, probe or test the vulnerability of the Platform, its users or any other system, or attempt to gain unauthorized access;',
              'use the Platform as a file-distribution service, a download repository, a content delivery network for third parties, a proxy or VPN, or for cryptocurrency mining;',
              'create accounts or generate content or requests in bulk with automated means, or resell access to AI features;',
              'circumvent plan limits, quotas, rate limits or security measures, including by creating several accounts or using temporary or disposable email addresses to get more free allowances;',
              'impersonate véltica, our staff or other users, or misrepresent your affiliation; or',
              'interfere with other people’s use of the Platform.',
            ],
          },
        ],
      },
      {
        title: 'Regulated and restricted activities',
        blocks: [
          'Some activities are regulated or carry higher risk, and we or our payment and domain providers may restrict or prohibit them. This includes gambling and games of chance, sweepstakes, financial and investment services, cryptocurrency, loans, regulated health products and services, alcohol, tobacco and vapor products, cannabis, firearms and dangerous goods, multi-level marketing, get-rich-quick offers and services that promise results they cannot deliver.',
          'If you take payments, you must also follow the business restrictions of our payment processor, Stripe, which apply in addition to this AUP. See the [Seller Terms](doc:sellers). If you are not sure whether what you want to do is allowed, ask us at {email} before you start.',
        ],
      },
      {
        title: 'Reporting a violation',
        blocks: [
          'If you see content or behavior that violates this policy, report it to {email} with the address of the Site or page, a description, and how to reach you. For claims of copyright infringement, use the process in the [Copyright Policy](doc:copyright). We review reports in good faith but are not obliged to act on every one, and we may not tell you the outcome.',
        ],
      },
      {
        title: 'How we enforce this policy',
        subs: [
          {
            title: 'What we may do',
            blocks: [
              'If we believe something violates this policy, we may, at our discretion and depending on the seriousness and history: ask you to fix it; remove or disable the content; unpublish a Site; limit features; suspend or terminate your Account; withhold or reverse a domain transfer; report the matter to the authorities; and take legal action. We may act immediately, without notice, when needed to protect people, the Platform or to comply with the law.',
              'Separately, the Platform applies automatic limits to protect everyone: on the size of what you save, and on how many form submissions, bookings, free claims and emails can come from one place or go to one address in a period. Reaching one of these limits is not a penalty; the action simply waits or is not sent.',
            ],
          },
          {
            title: 'Appeals',
            blocks: [
              'If you think we made a mistake, write to {email} explaining why. We will look again, but we are not obliged to reinstate content or an Account. Fees are not refunded when we act because of a violation.',
            ],
          },
        ],
      },
    ],
  },

  es: {
    title: 'Política de uso aceptable',
    summary: 'Lo que no puedes hacer con véltica y cómo actuamos ante las infracciones.',
    intro: [
      'Esta Política de uso aceptable (la «PUA») forma parte de los [Términos de servicio](doc:terms). Se aplica a tu Cuenta, tus Sitios, tu Contenido del cliente y todo lo que hagas con la Plataforma, incluido lo que tus Usuarios finales puedan hacer a través de ella. Podemos actualizarla como se describe en los Términos.',
    ],
    sections: [
      {
        title: 'La regla básica',
        blocks: [
          'Usa la Plataforma de forma lícita y de buena fe. No puedes usarla, ni permitir que otros la usen, de una manera que dañe a las personas, incumpla la ley, viole derechos ajenos o perjudique la Plataforma. Los ejemplos de abajo ilustran esta regla; no son una lista completa.',
        ],
      },
      {
        title: 'Contenido prohibido',
        blocks: [
          'No puedes publicar, almacenar, enlazar ni distribuir contenido que:',
          {
            list: [
              'explote sexualmente o ponga en peligro a menores de cualquier forma;',
              'sea ilegal, o promueva, facilite o celebre actividades ilegales;',
              'amenace, acose o incite a la violencia contra personas o grupos, o promueva el terrorismo o el extremismo violento;',
              'discrimine o incite al odio contra personas por su raza, etnia, origen nacional, religión, sexo, género, orientación sexual, discapacidad u otra característica protegida;',
              'sea fraudulento, engañoso o confuso, incluidos el phishing, la suplantación de una persona u organización, las reseñas falsas, los productos falsificados y los esquemas piramidales o Ponzi;',
              'infrinja derechos de autor, marcas, secretos comerciales, privacidad u otros derechos de terceros;',
              'contenga información privada de alguien sin su permiso, incluidas imágenes íntimas compartidas sin consentimiento;',
              'sea sexualmente explícito u ofrezca servicios sexuales para adultos;',
              'venda o promueva drogas ilegales, armas u otros bienes o servicios que sean ilegales donde tú o tus clientes estén; ni',
              'contenga o distribuya malware, spyware o código destinado a dañar dispositivos o robar datos.',
            ],
          },
        ],
      },
      {
        title: 'Comportamiento prohibido',
        blocks: [
          'No puedes usar la Plataforma para:',
          {
            list: [
              'enviar spam o mensajes comerciales no solicitados, ni usar formularios o reservas para enviar mensajes a personas que no los pidieron;',
              'recoger credenciales, números de tarjeta o datos sensibles de las personas mediante formularios diseñados para engañar o sin una finalidad lícita y los avisos exigidos;',
              'atacar, sobrecargar, sondear o probar la vulnerabilidad de la Plataforma, de sus usuarios o de cualquier otro sistema, ni intentar acceder sin autorización;',
              'usar la Plataforma como servicio de distribución de archivos, repositorio de descargas, red de entrega de contenido para terceros, proxy o VPN, ni para minar criptomonedas;',
              'crear cuentas o generar contenido o solicitudes en masa por medios automáticos, ni revender el acceso a las funciones de IA;',
              'eludir los límites del plan, las cuotas, los límites de frecuencia o las medidas de seguridad, incluso creando varias cuentas o usando correos temporales o desechables para obtener más cupos gratuitos;',
              'suplantar a véltica, a nuestro equipo o a otros usuarios, ni falsear tu vinculación; ni',
              'interferir con el uso de la Plataforma por otras personas.',
            ],
          },
        ],
      },
      {
        title: 'Actividades reguladas y restringidas',
        blocks: [
          'Algunas actividades están reguladas o implican más riesgo, y nosotros o nuestros proveedores de pagos y de dominios podemos restringirlas o prohibirlas. Esto incluye juegos de azar, sorteos, servicios financieros y de inversión, criptomonedas, préstamos, productos y servicios de salud regulados, alcohol, tabaco y vapeadores, cannabis, armas de fuego y mercancías peligrosas, marketing multinivel, ofertas para hacerse rico rápido y servicios que prometen resultados que no pueden cumplir.',
          'Si cobras, debes cumplir también las restricciones de negocio de nuestro procesador de pagos, Stripe, que aplican además de esta PUA. Consulta los [Términos de ventas y pagos](doc:sellers). Si no estás seguro de que lo que quieres hacer esté permitido, pregúntanos en {email} antes de empezar.',
        ],
      },
      {
        title: 'Cómo denunciar una infracción',
        blocks: [
          'Si ves contenido o conductas que incumplen esta política, denúncialo en {email} con la dirección del Sitio o la página, una descripción y cómo contactarte. Para reclamaciones por infracción de derechos de autor, usa el proceso de la [Política de derechos de autor](doc:copyright). Revisamos las denuncias de buena fe, pero no estamos obligados a actuar en todas y puede que no te informemos del resultado.',
        ],
      },
      {
        title: 'Cómo hacemos cumplir esta política',
        subs: [
          {
            title: 'Qué podemos hacer',
            blocks: [
              'Si creemos que algo incumple esta política, podemos, a nuestra discreción y según su gravedad e historial: pedirte que lo corrijas; retirar o desactivar el contenido; despublicar un Sitio; limitar funciones; suspender o cerrar tu Cuenta; retener o revertir la transferencia de un dominio; informar a las autoridades; y emprender acciones legales. Podemos actuar de inmediato, sin aviso, cuando haga falta para proteger a las personas o la Plataforma, o para cumplir la ley.',
              'Aparte, la Plataforma aplica límites automáticos para proteger a todos: al tamaño de lo que guardas y a cuántos envíos de formularios, reservas, ofertas gratuitas reclamadas y correos pueden salir de un mismo lugar o llegar a una misma dirección en un periodo. Llegar a uno de estos límites no es una sanción; la acción simplemente espera o no se envía.',
            ],
          },
          {
            title: 'Apelaciones',
            blocks: [
              'Si crees que nos equivocamos, escribe a {email} explicando por qué. Lo revisaremos de nuevo, pero no estamos obligados a restituir contenido ni una Cuenta. No se reembolsan las Tarifas cuando actuamos por una infracción.',
            ],
          },
        ],
      },
    ],
  },
}
